'use client'

import { useEffect } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { BroadcastChannel } from 'broadcast-channel'
import {
  BC_LOGOUT_EVENT,
  BC_PAGE_REFRESH_EVENT,
  BC_NAME,
  DID_LOGOUT_PARAM,
} from '@/modules/app/constants'
import { AUTH_BASE_PATH, MainRoutes } from '@/utils/routes'

const EVENT_IS_AUTHENTICATED = 'isAuthenticated'

export function TabManager({
  isAuthenticated: isCurrentPageAuthenticated,
}: {
  isAuthenticated: boolean
}) {
  const pathname = usePathname()
  const { replace } = useRouter()
  const searchParams = useSearchParams()
  const didLogout = searchParams.get(DID_LOGOUT_PARAM) === 'true'

  useEffect(() => {
    if (!didLogout) return

    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete(DID_LOGOUT_PARAM)
    const newUrl = `${MainRoutes.Home}?${newSearchParams.toString()}`
    window.history.replaceState({}, '', newUrl)
    const bc = new BroadcastChannel(BC_NAME)
    bc.postMessage({ type: BC_LOGOUT_EVENT })
    return () => {
      bc.close()
    }
  }, [didLogout, searchParams])

  useEffect(() => {
    // isAuthenticated is a SSR value, so isAuthenticated will never
    // change during client side navigation.
    const bc = new BroadcastChannel(BC_NAME)
    if (isCurrentPageAuthenticated) {
      bc.postMessage({
        type: BC_PAGE_REFRESH_EVENT,
        [EVENT_IS_AUTHENTICATED]: true,
      })
    } else {
      bc.postMessage({ type: BC_LOGOUT_EVENT })
    }

    return () => {
      bc.close()
    }
  }, [isCurrentPageAuthenticated])

  useEffect(() => {
    const bc = new BroadcastChannel(BC_NAME)
    bc.onmessage = (event) => {
      const {
        type: eventType,
        [EVENT_IS_AUTHENTICATED]: eventIsAuthenticated,
      } = event
      const shouldTriggerAuthComplete =
        eventType === BC_PAGE_REFRESH_EVENT &&
        eventIsAuthenticated &&
        !isCurrentPageAuthenticated

      const shouldTriggerLogoutEvent =
        eventType === BC_LOGOUT_EVENT && isCurrentPageAuthenticated

      if (shouldTriggerAuthComplete) {
        if (pathname.includes(`${AUTH_BASE_PATH}/`)) {
          replace(MainRoutes.AuthComplete)
        } else {
          window.location.reload()
        }
      } else if (shouldTriggerLogoutEvent) {
        window.location.reload()
      }
    }

    return () => {
      bc.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return null
}
